<template>
  <div class="notification-container">
    <!-- Dialogue de demande d'autorisation notifications -->
    <v-dialog v-model="showNotificationDialog" max-width="450px" persistent>
      <v-card class="notification-dialog">
        <div class="dialog-header">
          <div class="notification-icon">
            🔔
          </div>
          <v-card-title class="headline text-center">
            Enable Notifications
          </v-card-title>
        </div>
        
        <v-card-text class="dialog-content">
          <p class="dialog-subtitle">
            Stay updated with your PettyCash account!
          </p>
          
          <div class="benefits-list">
            <div class="benefit-item">
              <div class="benefit-icon">💬</div>
              <div class="benefit-text">Instant messages</div>
            </div>
            <div class="benefit-item">
              <div class="benefit-icon">🔔</div>
              <div class="benefit-text">Real-time notifications</div>
            </div>
            <div class="benefit-item">
              <div class="benefit-icon">📢</div>
              <div class="benefit-text">Important alerts</div>
            </div>
            <div class="benefit-item">
              <div class="benefit-icon">📥</div>
              <div class="benefit-text">Conversation updates</div>
            </div>
          </div>
          
          <p class="privacy-note">
            🔐 Your data remains private and secure
          </p>
        </v-card-text>
        
        <v-card-actions class="dialog-actions">
          <v-btn 
            text 
            color="grey darken-1"
            @click="denyNotifications" 
            :disabled="loading"
            class="deny-btn"
          >
            Maybe Later
          </v-btn>
          <v-btn 
            color="primary" 
            @click="requestNotificationPermission"
            :loading="loading"
            class="accept-btn"
            large
            depressed
          >
            <v-icon left>mdi-bell</v-icon>
            Allow Notifications
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackBar"
      :color="snackBarColor"
      :timeout="4000"
      bottom
      right
    >
      {{ snackBarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackBar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios"

export default {
  name: "NotificationDialog",
  data() {
    return {
      showNotificationDialog: true,
      loading: false
    }
  },
  computed: {
    snackBar: {
      get() {
        return this.$store.state.snackBar
      },
      set(value) {
        this.$store.state.snackBar = value
      }
    },
    snackBarColor() {
      return this.$store.state.snackBarColor
    },
    snackBarMessage() {
      return this.$store.state.snackBarMessage
    }
  },
  methods: {
    showSnackbar(message, color = "success") {
      this.$store.state.snackBarMessage = message
      this.$store.state.snackBarColor = color
      this.$store.state.snackBar = true
    },

    // Attendre que OneSignal soit chargé
    async waitForOneSignal(maxWait = 10000) {
      const startTime = Date.now()
      return new Promise((resolve, reject) => {
        const check = () => {
          if (window.OneSignal) return resolve()
          if (Date.now() - startTime > maxWait) return reject("OneSignal timeout")
          setTimeout(check, 100)
        }
        check()
      })
    },

    // Attendre que OneSignal renvoie un userId
    async waitForUserId(maxWait = 10000, interval = 200) {
      const startTime = Date.now()
      return new Promise((resolve, reject) => {
        const check = async () => {
          try {
            const userId = await window.OneSignal.getUserId()
            if (userId) return resolve(userId)
            if (Date.now() - startTime > maxWait) return reject("Timeout waiting for OneSignal userId")
            setTimeout(check, interval)
          } catch (err) {
            reject(err)
          }
        }
        check()
      })
    },
async requestNotificationPermission() {
  this.loading = true
  try {
    console.log("⏳ Waiting for OneSignal full initialization...")

    // Attendre que OneSignal init soit complet
    await new Promise((resolve) => {
      if (!window.OneSignal) {
        console.error("❌ OneSignal not loaded")
        return resolve()
      }
      window.OneSignal.push(() => {
        console.log("✅ OneSignal fully initialized")
        resolve()
      })
    })

    console.log("⏳ Requesting push permission...")
    const permission = await window.OneSignal.registerForPushNotifications({ modalPrompt: true })
    console.log("✅ Permission requested:", permission)

    // Attendre que OneSignal génère le userId
    const userId = await new Promise((resolve, reject) => {
      const start = Date.now()
      const interval = setInterval(async () => {
        try {
          const id = await window.OneSignal.getUserId()
          if (id) {
            clearInterval(interval)
            resolve(id)
          } else if (Date.now() - start > 10000) {
            clearInterval(interval)
            reject("Timeout waiting for OneSignal userId")
          }
        } catch (err) {
          clearInterval(interval)
          reject(err)
        }
      }, 200)
    })

    console.log("✅ Got OneSignal User ID:", userId)

    // Sauvegarder le userId côté backend
    await axios.post("/api/v1/manager/save-onesignal-user-id/", {
      user_id: userId,
      device_type: "web"
    }, { headers: { Authorization: "Token " + this.$store.state.token } })

    localStorage.setItem("onesignal_user_id", userId)
    this.showSnackbar("Notifications enabled successfully! 🎉", "success")
    this.showNotificationDialog = false

    // Redirection
    const originalPath = this.$route.query.from
    setTimeout(() => {
      if (originalPath) this.$router.push(originalPath)
      else this.$router.push({ name: "savings.home" })
    }, 1500)

  } catch (err) {
    console.error("❌ Error enabling notifications:", err)
    this.showSnackbar("Unable to enable notifications ❌", "error")
  } finally {
    this.loading = false
  }
},

    denyNotifications() {
      this.showNotificationDialog = false

      // Redirection
      const originalPath = this.$route.query.from
      if (originalPath) {
        this.$router.push(originalPath)
      } else {
        this.$router.push({ name: "savings.home" })
      }
      console.log("User denied notifications")
    }
  }
}
</script>



<style scoped>
.notification-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  font-family: 'Arial', sans-serif;
}

/* Styles pour le dialogue de notifications */
.notification-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  padding: 24px 24px 16px 24px;
  text-align: center;
}

.notification-icon {
  font-size: 48px;
  margin-bottom: 8px;
}

.dialog-header .headline {
  color: white !important;
  font-weight: 600;
  margin-bottom: 0;
}

.dialog-content {
  padding: 24px !important;
}

.dialog-subtitle {
  font-size: 16px;
  color: #666;
  text-align: center;
  margin-bottom: 24px;
  font-weight: 500;
}

.benefits-list {
  margin: 20px 0;
}

.benefit-item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px;
  background: rgba(59, 130, 246, 0.05);
  border-radius: 8px;
  border-left: 3px solid #3b82f6;
}

.benefit-icon {
  font-size: 20px;
  margin-right: 12px;
  width: 24px;
  text-align: center;
}

.benefit-text {
  font-size: 14px;
  color: #444;
  font-weight: 500;
}

.privacy-note {
  font-size: 12px;
  color: #888;
  text-align: center;
  margin-top: 16px;
  margin-bottom: 0;
  font-style: italic;
}

.dialog-actions {
  padding: 16px 24px 24px 24px !important;
  gap: 12px;
}

.deny-btn {
  text-transform: none !important;
  font-weight: 500;
}

.accept-btn {
  text-transform: none !important;
  font-weight: 600;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%) !important;
  color: white !important;
  flex: 1;
}

.accept-btn:hover {
  background: linear-gradient(135deg, #1e40af 0%, #2563eb 100%) !important;
}
</style>